import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { KycApiService, SendOtpResponse } from 'src/app/auth/services/kyc-api.service';
import { User } from 'src/app/shared/models/user.model';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { Country } from '../../interfaces/country.interface';
import { UpdateContactData } from '../../interfaces/update-contact-data';
import { CountriesService } from '../../services/countries.service';
import { UserService } from '../../services/user.service';
@Component({
  selector: 'app-edit-user-data',
  templateUrl: './edit-user-data.component.html',
  styleUrls: ['./edit-user-data.component.sass']
})
export class EditUserDataComponent implements OnInit, OnDestroy {
  datosForm: FormGroup | undefined;
  userData: User | undefined;
  subscription: Subscription = new Subscription();
  userSession: string = '';
  tiposID = ['CI', 'PASAPORTE', 'RUT'];
  paises: Country[]= [];
  sexos = ['Masculino', 'Femenino'];

  // --- Verificación del celular nuevo por WhatsApp ---
  // Si el cliente cambia el celular, antes de guardar se le manda un código al
  // número nuevo y se verifica contra el KYC; recién con el código correcto se
  // guarda en wallet-back como siempre. A ese número le llegan después los
  // códigos de su cuenta.
  /** Teléfono con el que abrió el formulario, solo dígitos (prefijo + número). */
  private originalPhoneDigits = '';
  otpStep = false;
  /** El código ya se verificó (y se consumió); solo falta guardar. */
  private otpVerified = false;
  otpPhone = '';
  otpCodeControl = new FormControl('', [Validators.required, Validators.pattern(/^\d{6}$/)]);
  otpError: string | null = null;
  otpLoading = false;
  otpCooldown = 0;
  private otpCooldownUntil = 0;
  private otpTimer: ReturnType<typeof setInterval> | null = null;
  /** Datos del formulario tal como estaban al pedir el código. */
  private pendingData: UpdateContactData | null = null;

  constructor (private dialogRef: MatDialogRef<EditUserDataComponent>,
    @Inject(MAT_DIALOG_DATA) data: any, private cognito: CognitoService, private fb: FormBuilder, private countryService: CountriesService, private userService: UserService, private spinner: SpinnerService,
    private kycApiService: KycApiService){
    this.initUserForm();
    this.userData = data;
    this.originalPhoneDigits = this.phoneDigits(data?.prefijoTelefono, data?.telefono);
    this.datosForm = this.fb.group({
      nombre: new FormControl({value: this.userData?.nombre, disabled: true }),
      apellido: new FormControl({value: this.userData?.apellidos, disabled: true }),
      tipoId: new FormControl({value: this.userData?.tipoId, disabled: true }),
      numeroId: new FormControl({value: this.userData?.numeroId, disabled: true }),
      nacionalidad: new FormControl({value: this.userData?.nacionalidad, disabled: true }),
      sexo: new FormControl({value: this.userData?.sexo === 'M' ? 'Masculino': 'Femenino', disabled: true }),
      fechaNacimiento: new FormControl({value: this.userData?.fechaNacimiento, disabled: true }),
      ciudadResidencia: new FormControl({value: this.userData?.ciudadResidencia, disabled: true }, [Validators.required]),
      paisResidencia: new FormControl({value: this.userData?.paisResidencia, disabled: true }, [Validators.required]),
      prefijoTelefono: new FormControl({value: this.userData?.prefijoTelefono ? this.userData?.prefijoTelefono : "+56", disabled: false }, [Validators.required]),
      telefono: new FormControl({value: this.userData?.telefono, disabled: false }, [Validators.required]),
      direccion: new FormControl(this.userData?.direccion, [Validators.required]),
      profesion: new FormControl({value: this.userData?.profesion, disabled: true}),
      recibeFactura: new FormControl({value: this.userData?.recibeFactura, disabled: false}, [Validators.required]),
    });

    this.countryService.getCountryList().subscribe( countryList => {
      this.paises = countryList;
    });
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.stopOtpTimer();
  }
  async initUserForm(){
    const userInSession = await this.cognito.getUser();
    this.userSession = userInSession.attributes['email'];
    this.subscription.add(this.userService.getUserData(this.userSession).subscribe(({user}: any)=>{
      this.userData = user;

    }))
  }
  ngOnInit(): void {

  }

  enviarDatos(){
    if (this.otpStep) {
      this.confirmOtp();
      return;
    }
    if( !this.datosForm?.valid) return;
    const userData: UpdateContactData = this.datosForm.value;
    userData.email = this.userSession;

    const newPhone = this.phoneDigits(userData.prefijoTelefono, userData.telefono);
    if (newPhone !== this.originalPhoneDigits) {
      void this.startOtp(userData);
      return;
    }
    this.save(userData);
  }

  /** Guarda. Si falla después de verificar el código, el diálogo queda abierto con el error. */
  private save(userData: UpdateContactData) {
    const spinnerRef = this.spinner.start();
    this.userService.updateUserData(userData).subscribe(({updated}: any)=>{

      this.spinner.stop(spinnerRef);
      this.dialogRef.close(true);
    }, error => {
      this.spinner.stop(spinnerRef);
      if (!this.otpStep) {
        this.dialogRef.close(false);
        return;
      }
      // El código ya se consumió al verificarlo: se reintenta solo el guardado.
      this.otpVerified = true;
      this.otpLoading = false;
      this.otpError = this.errorMessage(error);
    })
  }

  private async startOtp(userData: UpdateContactData) {
    this.pendingData = userData;
    this.otpPhone = `+${this.phoneDigits(userData.prefijoTelefono, userData.telefono)}`;
    this.otpError = null;
    this.otpLoading = true;
    const res = await this.kycApiService.sendOtp(this.otpPhone);
    this.otpLoading = false;
    if (!res.success && !res.expiresInSeconds) {
      // No llegó ningún código: se queda en el formulario con el motivo.
      this.otpError = this.sendErrorMessage(res);
      this.startCooldown(res.resendInSeconds ?? 0);
      return;
    }
    // Enviado, o rechazado pero con un código anterior todavía vigente.
    this.otpStep = true;
    this.otpVerified = false;
    this.otpCodeControl.reset('');
    this.otpError = res.success ? null : this.sendErrorMessage(res);
    this.datosForm?.get('prefijoTelefono')?.disable();
    this.datosForm?.get('telefono')?.disable();
    this.startCooldown(res.resendInSeconds ?? (res.success ? 60 : 0));
  }

  async resendOtp() {
    if (this.otpCooldown > 0 || this.otpLoading || !this.otpPhone) return;
    this.otpLoading = true;
    this.otpError = null;
    const res: SendOtpResponse = await this.kycApiService.resendOtp(this.otpPhone);
    this.otpLoading = false;
    if (!res.success) this.otpError = this.sendErrorMessage(res);
    this.startCooldown(res.resendInSeconds ?? (res.success ? 60 : 0));
  }

  /**
   * Con WhatsApp caído el cliente no puede verificar el celular por su cuenta:
   * se lo deriva al operador en vez del mensaje genérico del servicio.
   */
  private sendErrorMessage(res: SendOtpResponse): string {
    if (res.code === 'UNAVAILABLE') {
      return 'Para actualizar tu celular, comunícate con el operador para activar el OTP.';
    }
    return res.message || 'No pudimos enviar el código por WhatsApp.';
  }

  private async confirmOtp() {
    if (!this.pendingData || this.otpLoading) return;
    // Si ya se verificó y falló solo el guardado, no se vuelve a pedir el código.
    if (this.otpVerified) {
      this.otpLoading = true;
      this.otpError = null;
      this.save(this.pendingData);
      return;
    }
    const code = (this.otpCodeControl.value || '').trim();
    if (!/^\d{6}$/.test(code)) {
      this.otpError = 'El código tiene 6 dígitos.';
      return;
    }
    this.otpLoading = true;
    this.otpError = null;
    // verifyOtp nunca lanza: hay que mirar `success`.
    const res = await this.kycApiService.verifyOtp(this.otpPhone, code);
    if (!res.success) {
      this.otpLoading = false;
      this.otpError = res.message || 'El código no es válido.';
      // Sin intentos o vencido: el código ya no sirve, hay que pedir otro.
      if (res.code === 'BLOCKED' || res.code === 'EXPIRED' || res.code === 'NO_PENDING') {
        this.otpCodeControl.reset('');
      }
      return;
    }
    this.otpVerified = true;
    this.save(this.pendingData);
  }

  /** Vuelve al formulario para corregir el número. */
  cancelOtp() {
    this.otpStep = false;
    this.otpVerified = false;
    this.otpError = null;
    this.pendingData = null;
    this.stopOtpTimer();
    this.otpCooldown = 0;
    this.datosForm?.get('prefijoTelefono')?.enable();
    this.datosForm?.get('telefono')?.enable();
  }

  // El contador se calcula contra la hora de vencimiento y no restando de a
  // uno: en el celular la pestaña se congela mientras el cliente va a WhatsApp.
  private startCooldown(seconds: number) {
    this.stopOtpTimer();
    this.otpCooldownUntil = Date.now() + Math.max(0, seconds) * 1000;
    this.tickCooldown();
    if (this.otpCooldown > 0) {
      this.otpTimer = setInterval(() => this.tickCooldown(), 1000);
    }
  }

  private tickCooldown() {
    this.otpCooldown = Math.max(0, Math.ceil((this.otpCooldownUntil - Date.now()) / 1000));
    if (this.otpCooldown === 0) this.stopOtpTimer();
  }

  private stopOtpTimer() {
    if (this.otpTimer) {
      clearInterval(this.otpTimer);
      this.otpTimer = null;
    }
  }

  private phoneDigits(prefijo: unknown, telefono: unknown): string {
    const d = (v: unknown) => String(v ?? '').replace(/\D/g, '');
    return d(prefijo) + d(telefono);
  }

  private errorMessage(error: any): string {
    // 400 llega como HttpErrorResponse; 503 y otros, como texto (ErrorInterceptor).
    if (typeof error === 'string' && error) return error;
    const msg = error?.error?.message;
    if (Array.isArray(msg)) return msg.join('. ');
    return msg || 'No pudimos guardar los datos. Intenta nuevamente.';
  }

  close() {
    this.dialogRef.close();
}

}
